import React from 'react';
import { ClerkProvider } from '@clerk/clerk-react';
import { useTheme } from '../context/ThemeContext';

const CLERK_PUBLISHABLE_KEY = 'pk_test_c21pbGluZy1mbG91bmRlci01Ni5jbGVyay5hY2NvdW50cy5kZXYk';

// Clerk appearance configuration for dark and light themes
const getClerkAppearance = (theme) => ({
  variables: {
    colorPrimary: theme === 'dark' ? '#39ff14' : '#10b981',
    colorBackground: theme === 'dark' ? '#091619' : '#ffffff',
    colorInputBackground: theme === 'dark' ? '#020a07' : '#f8fafc',
    colorInputText: theme === 'dark' ? '#f5f5f5' : '#0f172a',
    colorText: theme === 'dark' ? '#f5f5f5' : '#0f172a',
    colorTextSecondary: theme === 'dark' ? '#94a3b8' : '#64748b',
    colorTextOnPrimaryBackground: theme === 'dark' ? '#020a07' : '#ffffff',
    colorDanger: '#ef4444',
    colorSuccess: '#10b981',
    colorWarning: '#f59e0b',
    borderRadius: '4px',
  },
  elements: {
    // Root and card elements
    rootBox: {
      backgroundColor: theme === 'dark' ? '#091619' : '#ffffff',
    },
    card: {
      backgroundColor: theme === 'dark' ? '#091619' : '#ffffff',
      borderRadius: '8px',
      boxShadow: theme === 'dark'
        ? '0 20px 25px -5px rgba(0, 0, 0, 0.5)'
        : '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      border: theme === 'dark' ? '1px solid rgba(56, 248, 137, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
    },
    // Modal content
    modalContent: {
      backgroundColor: theme === 'dark' ? '#091619' : '#ffffff',
      border: theme === 'dark' ? '1px solid rgba(56, 248, 137, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
    },
    modalBackdrop: {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
    },
    // Navbar (sidebar in account modal)
    navbar: {
      backgroundColor: theme === 'dark' ? '#020a07' : '#f8fafc',
      borderRight: theme === 'dark' ? '1px solid rgba(56, 248, 137, 0.15)' : '1px solid rgba(0, 0, 0, 0.1)',
    },
    navbarButton: {
      color: theme === 'dark' ? '#94a3b8' : '#475569',
      '&:hover': {
        backgroundColor: theme === 'dark' ? 'rgba(11, 98, 88, 0.5)' : 'rgba(16, 185, 129, 0.1)',
      },
    },
    navbarButton__active: {
      backgroundColor: theme === 'dark' ? 'rgba(11, 98, 88, 0.7)' : 'rgba(16, 185, 129, 0.15)',
      color: theme === 'dark' ? '#39ff14' : '#059669',
      '&:hover': {
        backgroundColor: theme === 'dark' ? 'rgba(11, 98, 88, 0.8)' : 'rgba(16, 185, 129, 0.2)',
      },
    },
    navbarButtonIcon: {
      color: theme === 'dark' ? '#94a3b8' : '#475569',
    },
    navbarButtonIcon__active: {
      color: theme === 'dark' ? '#39ff14' : '#059669',
    },
    navbarButtonText: {
      color: theme === 'dark' ? '#94a3b8' : '#475569',
    },
    navbarButtonText__active: {
      color: theme === 'dark' ? '#39ff14' : '#059669',
    },
    // Page content
    page: {
      backgroundColor: theme === 'dark' ? '#091619' : '#ffffff',
    },
    pageScrollBox: {
      backgroundColor: theme === 'dark' ? '#091619' : '#ffffff',
    },
    // Headers
    headerTitle: {
      color: theme === 'dark' ? '#f5f5f5' : '#0f172a',
    },
    headerSubtitle: {
      color: theme === 'dark' ? '#94a3b8' : '#64748b',
    },
    // Profile sections
    profileSection: {
      backgroundColor: theme === 'dark' ? '#091619' : '#ffffff',
    },
    profileSectionTitle: {
      color: theme === 'dark' ? '#f5f5f5' : '#0f172a',
    },
    profileSectionTitleText: {
      color: theme === 'dark' ? '#f5f5f5' : '#0f172a',
    },
    profileSectionContent: {
      backgroundColor: theme === 'dark' ? '#091619' : '#ffffff',
    },
    profileSectionPrimaryButton: {
      backgroundColor: theme === 'dark' ? 'rgba(11, 98, 88, 0.5)' : 'rgba(16, 185, 129, 0.1)',
      color: theme === 'dark' ? '#39ff14' : '#059669',
      border: theme === 'dark' ? '1px solid rgba(56, 248, 137, 0.3)' : '1px solid rgba(16, 185, 129, 0.3)',
      '&:hover': {
        backgroundColor: theme === 'dark' ? 'rgba(11, 98, 88, 0.7)' : 'rgba(16, 185, 129, 0.2)',
      },
    },
    // Form elements
    formFieldLabel: {
      color: theme === 'dark' ? '#f5f5f5' : '#0f172a',
    },
    formFieldInput: {
      backgroundColor: theme === 'dark' ? '#020a07' : '#f8fafc',
      color: theme === 'dark' ? '#f5f5f5' : '#0f172a',
      border: theme === 'dark' ? '1px solid rgba(56, 248, 137, 0.2)' : '1px solid rgba(0, 0, 0, 0.15)',
      '&:focus': {
        borderColor: theme === 'dark' ? 'rgba(56, 248, 137, 0.5)' : 'rgba(16, 185, 129, 0.5)',
        boxShadow: theme === 'dark'
          ? '0 0 0 3px rgba(56, 248, 137, 0.1)'
          : '0 0 0 3px rgba(16, 185, 129, 0.1)',
      },
    },
    formButtonPrimary: {
      backgroundColor: theme === 'dark' ? 'rgba(11, 98, 88, 0.7)' : '#10b981',
      color: theme === 'dark' ? '#39ff14' : '#ffffff',
      border: theme === 'dark' ? '1px solid rgba(56, 248, 137, 0.3)' : 'none',
      '&:hover': {
        backgroundColor: theme === 'dark' ? 'rgba(11, 98, 88, 0.9)' : '#059669',
      },
    },
    formButtonReset: {
      color: theme === 'dark' ? '#94a3b8' : '#64748b',
    },
    // Identity preview
    identityPreview: {
      backgroundColor: theme === 'dark' ? '#020a07' : '#f8fafc',
      border: theme === 'dark' ? '1px solid rgba(56, 248, 137, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
    },
    identityPreviewText: {
      color: theme === 'dark' ? '#f5f5f5' : '#0f172a',
    },
    identityPreviewEditButton: {
      color: theme === 'dark' ? '#39ff14' : '#059669',
    },
    identityPreviewEditButtonIcon: {
      color: theme === 'dark' ? '#39ff14' : '#059669',
    },
    // Accordions
    accordionTriggerButton: {
      color: theme === 'dark' ? '#f5f5f5' : '#0f172a',
      backgroundColor: theme === 'dark' ? '#091619' : '#ffffff',
      '&:hover': {
        backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
      },
    },
    accordionContent: {
      backgroundColor: theme === 'dark' ? '#091619' : '#ffffff',
    },
    // Badges
    badge: {
      backgroundColor: theme === 'dark' ? 'rgba(56, 248, 137, 0.2)' : 'rgba(16, 185, 129, 0.15)',
      color: theme === 'dark' ? '#39ff14' : '#059669',
    },
    // Buttons and actions
    button: {
      color: theme === 'dark' ? '#f5f5f5' : '#0f172a',
    },
    actionCard: {
      backgroundColor: theme === 'dark' ? '#020a07' : '#f8fafc',
      border: theme === 'dark' ? '1px solid rgba(56, 248, 137, 0.15)' : '1px solid rgba(0, 0, 0, 0.1)',
    },
    // Footer
    footer: {
      backgroundColor: theme === 'dark' ? '#020a07' : '#f8fafc',
      borderTop: theme === 'dark' ? '1px solid rgba(56, 248, 137, 0.15)' : '1px solid rgba(0, 0, 0, 0.1)',
    },
    footerAction: {
      color: theme === 'dark' ? '#94a3b8' : '#64748b',
    },
    footerActionLink: {
      color: theme === 'dark' ? '#39ff14' : '#059669',
    },
    // User button popover
    userButtonPopoverCard: {
      backgroundColor: theme === 'dark' ? '#091619' : '#ffffff',
      border: theme === 'dark' ? '1px solid rgba(56, 248, 137, 0.3)' : '1px solid rgba(0, 0, 0, 0.15)',
      boxShadow: theme === 'dark'
        ? '0 10px 25px -5px rgba(0, 0, 0, 0.5)'
        : '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
    },
    userButtonPopoverMain: {
      backgroundColor: theme === 'dark' ? '#091619' : '#ffffff',
    },
    userButtonPopoverFooter: {
      display: 'none',
    },
    userButtonPopoverActionButton: {
      color: theme === 'dark' ? '#f5f5f5' : '#0f172a',
      '&:hover': {
        backgroundColor: theme === 'dark' ? 'rgba(11, 98, 88, 0.7)' : 'rgba(16, 185, 129, 0.15)',
        color: theme === 'dark' ? '#39ff14' : '#059669',
      },
    },
    userButtonPopoverActionButtonText: {
      color: theme === 'dark' ? '#f5f5f5' : '#0f172a',
    },
    userButtonPopoverActionButtonIcon: {
      color: theme === 'dark' ? '#f5f5f5' : '#0f172a',
    },
    userPreviewMainIdentifier: {
      color: theme === 'dark' ? '#f5f5f5' : '#0f172a',
    },
    userPreviewSecondaryIdentifier: {
      color: theme === 'dark' ? 'rgba(245, 245, 245, 0.7)' : '#64748b',
    },
    // Menu buttons
    menuButton: {
      color: theme === 'dark' ? '#94a3b8' : '#64748b',
      '&:hover': {
        backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
      },
    },
    menuList: {
      backgroundColor: theme === 'dark' ? '#091619' : '#ffffff',
      border: theme === 'dark' ? '1px solid rgba(56, 248, 137, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
    },
    menuItem: {
      color: theme === 'dark' ? '#f5f5f5' : '#0f172a',
      '&:hover': {
        backgroundColor: theme === 'dark' ? 'rgba(11, 98, 88, 0.5)' : 'rgba(16, 185, 129, 0.1)',
      },
    },
    // Table elements (for connected accounts, etc.)
    tableHead: {
      backgroundColor: theme === 'dark' ? '#020a07' : '#f8fafc',
    },
    // Alert elements
    alert: {
      backgroundColor: theme === 'dark' ? 'rgba(56, 248, 137, 0.1)' : 'rgba(16, 185, 129, 0.1)',
      border: theme === 'dark' ? '1px solid rgba(56, 248, 137, 0.2)' : '1px solid rgba(16, 185, 129, 0.2)',
    },
    alertText: {
      color: theme === 'dark' ? '#f5f5f5' : '#0f172a',
    },
    // Dividers
    dividerLine: {
      backgroundColor: theme === 'dark' ? 'rgba(56, 248, 137, 0.15)' : 'rgba(0, 0, 0, 0.1)',
    },
    dividerText: {
      color: theme === 'dark' ? '#94a3b8' : '#64748b',
    },
  },
});

export default function ClerkThemeProvider({ children }) {
  const { theme } = useTheme();

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      afterSignOutUrl="/"
      appearance={getClerkAppearance(theme)}
    >
      {children}
    </ClerkProvider>
  );
}

